import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import {
  resetPasswordRequestUserMutation,
  resetPasswordUserMutation,
  signInUserMutation,
  updateCourseIntroStatusUserMutation,
  updateZeroLessonStatusUserMutation,
} from '~/shared/graphql-queries';
import cancelEmailChangeUserMutation from '~/shared/graphql-queries/users/cancel-email-change-user-mutation';
import confirmEmailChangeUserMutation from '~/shared/graphql-queries/users/confirm-email-change-user-mutation';
import createTelegramConnectLinkMutation from '~/shared/graphql-queries/users/create-telegram-connect-link-mutation';
import disconnectTelegramMutation from '~/shared/graphql-queries/users/disconnect-telegram-mutation';
import findByIdUserQuery from '~/shared/graphql-queries/users/find-by-id-user-query';
import findManyUserQuery from '~/shared/graphql-queries/users/find-many-user-query';
import getAuthInfoUserQuery from '~/shared/graphql-queries/users/get-auth-info-user-query';
import navigationOnBehalfUserMutation from '~/shared/graphql-queries/users/navigation-on-behalf-user-mutation';
import registerUserMutation from '~/shared/graphql-queries/users/register-user-mutation';
import removeByIdUserMutation from '~/shared/graphql-queries/users/remove-by-id-user-mutation';
import requestEmailChangeUserMutation from '~/shared/graphql-queries/users/request-email-change-user-mutation';
import setActivationEmailUserMutation from '~/shared/graphql-queries/users/set-activation-email-user-mutation';
import studentsAttentionCountQuery from '~/shared/graphql-queries/users/students-attention-count-query';
import updateByIdUserMutation from '~/shared/graphql-queries/users/update-by-id-user-mutation';
import updateStudentLevelUserMutation from '~/shared/graphql-queries/users/update-student-level-user-mutation';
import updateTelegramSettingsMutation from '~/shared/graphql-queries/users/update-telegram-settings-mutation';
import userFragment from '~/shared/graphql-queries/users/user-fragment';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type RequestEmailChangeUserArgs = Readonly<{
  email?: string;
  password?: string;
  hideFlashMessage?: boolean;
}>;

type EmailChangeTokenArgs = Readonly<{
  token?: string;
  hideFlashMessage?: boolean;
}>;
type PaginationUserQueryResponse = Readonly<{
  data?: {
    paginationUserQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationUserArgs = Readonly<{
  filter?: Record<string, unknown>;
  filterType?: string;
  nextPage?: number;
  sort?: string;
}>;

type UpdateByIdUserArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
  hideFlashMessage?: boolean;
}>;

type RegisterUserArgs = Readonly<{
  courses?: unknown[];
  company?: unknown;
  email?: string;
  role?: string;
  dbId?: string;
  age?: number;
  manager?: unknown;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  login?: string;
  password?: string;
  isEmailVerificationEnabled?: boolean;
  hideFlashMessage?: boolean;
}>;

type ResetPasswordUserArgs = Readonly<{
  newPassword?: string;
  codeForResetPassword?: string;
  hideFlashMessage?: boolean;
}>;

type SignInUserArgs = Readonly<{
  password?: string;
  login?: string;
  hideFlashMessage?: boolean;
}>;

type ResetPasswordRequestUserArgs = Readonly<{
  email?: string;
  hideFlashMessage?: boolean;
}>;

type UpdateCourseIntroStatusUserArgs = Readonly<{
  scope?: string;
  completed?: boolean;
  dismissed?: boolean;
  lastStep?: number;
  hideFlashMessage?: boolean;
}>;

type RemoveByIdUserArgs = Readonly<{
  _id?: string;
}>;

type FindManyUserArgs = Readonly<{
  limit?: number;
  filter?: Record<string, unknown>;
}>;

type NavigationOnBehalfUserArgs = Readonly<{
  _id?: string;
  course?: string;
  sessionKey?: string;
}>;

type UpdateStudentLevelUserArgs = Readonly<{
  records?: unknown[];
  courseCategoryId?: string;
}>;

type FindByIdUserArgs = Readonly<{
  _id?: string;
}>;

type PaginationUserResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type StudentsAttentionCountResult = Readonly<{
  awaitingTrial: number;
  needModule: number;
  total: number;
}>;

type StudentsAttentionCountResponse = Readonly<{
  data?: {
    studentsAttentionCountQuery?: StudentsAttentionCountResult | null;
  };
}>;

type TelegramNotificationSettingsInput = Readonly<{
  zeroLessonCreated?: boolean;
  zeroLessonRescheduled?: boolean;
  lessonReminder?: boolean;
  scheduleChanged?: boolean;
  extraLessonCreated?: boolean;
  teacherChanged?: boolean;
  marketing?: boolean;
}>;

type CreateTelegramConnectLinkArgs = Readonly<{
  userId?: string;
  codeForActivation?: string;
  notificationSettings?: TelegramNotificationSettingsInput;
  hideFlashMessage?: boolean;
}>;

type CreateTelegramConnectLinkResult = Readonly<{
  data?: {
    createTelegramConnectLink?: {
      connectUrl: string;
      expiresAt?: string | null;
    };
  };
}>;

type UpdateTelegramSettingsArgs = Readonly<{
  record: TelegramNotificationSettingsInput;
  hideFlashMessage?: boolean;
}>;

const transformPaginationUserResponse =
  (type: 'paginationUserQuery') =>
  (response: PaginationUserQueryResponse): PaginationUserResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const usersTagTypes = ['users', 'user', 'students-attention'] as const;

export const getUsersQuery = (builder: BuilderType) => {
  return {
    studentsAttentionCount: builder.query<StudentsAttentionCountResult, void>({
      query: () => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(studentsAttentionCountQuery),
          variables: {},
        }),
      }),
      transformResponse: (response: StudentsAttentionCountResponse): StudentsAttentionCountResult =>
        response.data?.studentsAttentionCountQuery ?? {
          awaitingTrial: 0,
          needModule: 0,
          total: 0,
        },
      providesTags: ['students-attention'],
    }),
    paginationUser: builder.query<PaginationUserResult, PaginationUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'user',
              fragment: userFragment,
              filterType: args?.filterType,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_DESC,
            perPage: LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['users'],
      transformResponse: transformPaginationUserResponse('paginationUserQuery'),
    }),
    requestEmailChangeUser: builder.mutation<unknown, RequestEmailChangeUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(requestEmailChangeUserMutation),
          variables: {
            email: args.email,
            password: args.password,
          },
        }),
        meta: { hideFlashMessage },
      }),
      invalidatesTags: ['user'],
    }),

    confirmEmailChangeUser: builder.mutation<unknown, EmailChangeTokenArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(confirmEmailChangeUserMutation),
          variables: {
            token: args.token,
          },
        }),
        meta: { hideFlashMessage },
      }),
      invalidatesTags: ['user'],
    }),

    cancelEmailChangeUser: builder.mutation<unknown, EmailChangeTokenArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(cancelEmailChangeUserMutation),
          variables: {
            token: args.token,
          },
        }),
        meta: { hideFlashMessage },
      }),
      invalidatesTags: ['user'],
    }),
    updateByIdUser: builder.mutation<unknown, UpdateByIdUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdUserMutation),
          variables: {
            _id: args._id,
            record: args.record,
            showSuccessMessage: args.showSuccessMessage,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    registerUser: builder.mutation<unknown, RegisterUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(registerUserMutation),
          variables: {
            courses: args.courses,
            company: args.company,
            email: args.email,
            role: args.role,
            dbId: args.dbId,
            age: args.age,
            manager: args.manager,
            firstName: args.firstName,
            lastName: args.lastName,
            phone: args.phone,
            login: args.login,
            password: args.password,
            isEmailVerificationEnabled: args.isEmailVerificationEnabled,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    resetPasswordUser: builder.mutation<unknown, ResetPasswordUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(resetPasswordUserMutation),
          variables: {
            newPassword: args.newPassword,
            codeForResetPassword: args.codeForResetPassword,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    signInUser: builder.mutation<unknown, SignInUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(signInUserMutation),
          variables: {
            password: args.password,
            login: args.login,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    createTelegramConnectLink: builder.mutation<CreateTelegramConnectLinkResult, CreateTelegramConnectLinkArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createTelegramConnectLinkMutation),
          variables: {
            userId: args.userId,
            codeForActivation: args.codeForActivation,
            notificationSettings: args.notificationSettings,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    disconnectTelegram: builder.mutation<unknown, { hideFlashMessage?: boolean } | void>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(disconnectTelegramMutation),
        }),
        meta: { hideFlashMessage: args?.hideFlashMessage ?? false },
      }),
      invalidatesTags: ['user'],
    }),

    updateTelegramSettings: builder.mutation<unknown, UpdateTelegramSettingsArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateTelegramSettingsMutation),
          variables: {
            record: args.record,
          },
        }),
        meta: { hideFlashMessage },
      }),
      invalidatesTags: ['user'],
    }),

    resetPasswordRequestUser: builder.mutation<unknown, ResetPasswordRequestUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(resetPasswordRequestUserMutation),
          variables: {
            email: args.email,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    updateCourseIntroStatusUser: builder.mutation<unknown, UpdateCourseIntroStatusUserArgs>({
      query: ({ hideFlashMessage = false, ...args }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateCourseIntroStatusUserMutation),
          variables: {
            scope: args.scope,
            completed: args.completed,
            dismissed: args.dismissed,
            lastStep: args.lastStep,
          },
        }),
        meta: { hideFlashMessage },
      }),
    }),

    removeByIdUser: builder.mutation<unknown, RemoveByIdUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdUserMutation),
          variables: {
            _id: args._id,
          },
        }),
      }),
    }),

    findManyUser: builder.query<unknown, FindManyUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findManyUserQuery),
          variables: {
            limit: args.limit,
            filter: args.filter,
          },
        }),
      }),
    }),

    navigationOnBehalfUser: builder.mutation<unknown, NavigationOnBehalfUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(navigationOnBehalfUserMutation),
          variables: {
            _id: args._id,
            course: args.course,
            sessionKey: args.sessionKey,
          },
        }),
      }),
    }),

    updateStudentLevelUser: builder.mutation<unknown, UpdateStudentLevelUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateStudentLevelUserMutation),
          variables: {
            records: args.records,
            courseCategoryId: args.courseCategoryId,
          },
        }),
      }),
        invalidatesTags: ['students-attention'],
    }),
    setActivationEmailUser: builder.mutation<unknown, UpdateStudentLevelUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(setActivationEmailUserMutation),
          variables: {
            email: args.email,
          },
        }),
      }),
    }),
    updateZeroLessonStatusUser: builder.mutation<unknown, UpdateStudentLevelUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateZeroLessonStatusUserMutation),
          variables: {
            studentId: args.studentId,
            courseId: args.courseId,
            zeroLessonStatus: args.zeroLessonStatus,
          },
        }),
      }),
        invalidatesTags: ['students-attention'],
    }),

    findByIdUser: builder.query<unknown, FindByIdUserArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByIdUserQuery),
          variables: {
            _id: args._id,
          },
        }),
      }),
      providesTags: ['user'],
    }),

    getAuthInfoUser: builder.query<unknown, void>({
      query: () => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(getAuthInfoUserQuery),
        }),
      }),
    }),
  };
};
